// ./src/contexts/GoogleMapsContext/GoogleMapsContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from "react";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";

export interface GoogleMapsContextValue {
  isLoaded: boolean;
  isLoading: boolean;
  error: Error | null;
  loadLibraries: <T extends string>(libraries: T[]) => Promise<Record<T, any>>;
  getCoreLibrary: () => typeof google.maps | null;
}

export interface GoogleMapsProviderOptions {
  libraries?: string[];
}

export interface GoogleMapsProviderProps {
  children: React.ReactNode;
  options?: GoogleMapsProviderOptions;
}

const defaultContextValue: GoogleMapsContextValue = {
  isLoaded: false,
  isLoading: false,
  error: null,
  loadLibraries: async () => {
    throw new Error("GoogleMapsProvider not initialized");
  },
  getCoreLibrary: () => null,
};

const GoogleMapsContext =
  createContext<GoogleMapsContextValue>(defaultContextValue);

export const GoogleMapsProvider: React.FC<GoogleMapsProviderProps> = ({
  children,
  options = {},
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const loadedLibrariesRef = useRef<Map<string, any>>(new Map());
  const hasAutoLoadedRef = useRef(false);
  const hasInitializedRef = useRef(false);

  const initializeOptions = useCallback(() => {
    if (!hasInitializedRef.current) {
      setOptions({
        key: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
        v: "weekly",
      });
      hasInitializedRef.current = true;
    }
  }, []);

  const getCoreLibrary = useCallback(() => {
    return typeof google !== "undefined" && google.maps ? google.maps : null;
  }, []);

  const loadLibraries = useCallback(
    async <T extends string>(libraries: T[]): Promise<Record<T, any>> => {
      try {
        initializeOptions();

        setIsLoading(true);
        setError(null);

        const result: Record<string, any> = {};

        // Load libraries
        for (const lib of libraries) {
          // Check if already loaded
          if (loadedLibrariesRef.current.has(lib)) {
            result[lib] = loadedLibrariesRef.current.get(lib);
          } else {
            const loadedLib = await importLibrary(lib as any);
            loadedLibrariesRef.current.set(lib, loadedLib);
            result[lib] = loadedLib;
          }
        }

        // Set loaded flag if not already set
        if (!isLoaded) {
          setIsLoaded(true);
        }

        return result as Record<T, any>;
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error("Failed to load Google Maps");
        setError(error);
        console.error("Error loading Google Maps:", error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [isLoaded, initializeOptions]
  );

  // Autoload libraries on mount if specified in options
  React.useEffect(() => {
    if (
      options.libraries &&
      options.libraries.length > 0 &&
      !hasAutoLoadedRef.current
    ) {
      hasAutoLoadedRef.current = true;
      loadLibraries(options.libraries);
    }
  }, [options.libraries, loadLibraries]);

  const value: GoogleMapsContextValue = {
    isLoaded,
    isLoading,
    error,
    loadLibraries,
    getCoreLibrary,
  };

  return (
    <GoogleMapsContext.Provider value={value}>
      {children}
    </GoogleMapsContext.Provider>
  );
};

// Custom hook for using the Google Maps context
export const useGoogleMapsContext = (): GoogleMapsContextValue => {
  const context = useContext(GoogleMapsContext);
  return context;
};
